import React, {
  createRef, useEffect, useRef, useState,
} from 'react';

import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import {
  darken, IconButton, useMediaQuery, useTheme,
} from '@material-ui/core';
import { useTranslation } from 'next-i18next';
import Spacer from '@/components/ui-components/Spacer';
import useDragScroll from '@/hooks/useDragScroll';
import VerticalTimeline, {
  ElRefsType,
  EventType,
  SVGIcon,
} from './VerticalTimeline';
import Typography from '../../components/Typography';
import useWindowSize from '@/hooks/useWindowSize';

interface StyleProps {
  isSwiping: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  horizontalTimelineRoot: {
    cursor: ({ isSwiping }: StyleProps) => (isSwiping ? 'grabbing' : 'grab'),
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    overflowX: 'auto',
    msOverflowStyle: 'none' /* IE and Edge */,
    scrollbarWidth: 'none' /* Firefox */,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    flexWrap: 'nowrap',
    width: '100%',
    direction: 'rtl',
    paddingBottom: theme.spacing(3),
    // height: 200,
  },
  horizontalTimelineBar: {
    backgroundColor: '#A9ABB5',
    height: theme.spacing(1.5),
    width: '100%',
  },
  horizontalTimelineBarCurrentRound: {
    backgroundColor: theme.palette.primary.main,
  },
  horizontalTimelineVerticalBar: {
    marginLeft: 15,
    backgroundColor: '#A9ABB5',
    height: theme.spacing(10),
    width: 2,
  },
  horizontalTimelineBarLastItem: {
    borderTopRightRadius: 180,
    borderBottomRightRadius: 180,
  },
  horizontalTimelineBarFirstItem: {
    borderTopLeftRadius: 180,
    borderBottomLeftRadius: 180,
  },
  horizontalTimelineVerticalBarCurrentRound: {
    backgroundColor: theme.palette.primary.main,
  },
  horizontalTimelineIcon: {
    width: 32,
    height: 32,
    borderRadius: 180,
    backgroundColor: theme.palette.greyScale.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    scrollBehavior: 'smooth',
  },
  horizontalTimelineIconCurrentRound: {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: darken(theme.palette.primary.main, 0.3),
    },
  },
  horizontalTimelineIconLastRounds: {
    '&:hover': {
      backgroundColor: darken(theme.palette.greyScale.main, 0.3),
    },
  },
}));

interface Props {
  events: EventType[];
}

const defaultProps = {};

const YEARS = [
  0.0,
  31536000000,
  63072000000,
  94694400000,
  126230400000,
  157766400000,
  189302400000,
  220924800000,
  252460800000,
  283996800000,
  315532800000,
  347155200000,
  378691200000,
  410227200000,
  441763200000,
  473385600000,
  504921600000,
  536457600000,
  567993600000,
  599616000000,
  631152000000,
  662688000000,
  694224000000,
  725846400000,
  757382400000,
  788918400000,
  820454400000,
  852076800000,
  883612800000,
  915148800000,
  946684800000,
  978307200000,
  1009843200000,
  1041379200000,
  1072915200000,
  1104537600000,
  1136073600000,
  1167609600000,
  1199145600000,
  1230768000000,
  1262304000000,
  1293840000000,
  1325376000000,
  1356998400000,
  1388534400000,
  1420070400000,
  1451606400000,
  1483228800000,
  1514764800000,
  1546300800000,
  1577836800000,
  1609459200000,
  1640995200000,
  1672531200000,
  1704067200000,
  1735689600000,
  1767225600000,
  1798761600000,
  1830297600000,
  1861920000000,
  1893456000000,
  1924992000000,
  1956528000000,
  1988150400000,
  2019686400000,
  2051222400000,
  2082758400000,
  2114380800000,
  2145916800000,
  2177452800000,
  2208988800000,
  2240611200000,
  2272147200000,
  2303683200000,
  2335219200000,
  2366841600000,
  2398377600000,
  2429913600000,
  2461449600000,
  2493072000000,
];

const Timeline = ({ events }: Props) => {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('markets');
  // calculate some math helpers
  const eventsReverse = [...events].reverse();
  const today = Date.now();
  const distanceFromToday = Math.abs(today - events[0].timestamp);
  // closest distance between items
  const closestDistance = events.reduce((acc, curr, currIndex) => {
    const distanceToLast = currIndex === 0
      ? acc
      : Math.abs(curr.timestamp - events[currIndex - 1].timestamp);
    if (distanceToLast < acc && distanceToLast > 7 * 24 * 3600 * 1000) {
      return distanceToLast;
    }
    return acc;
    //   minimum is 7 days
  }, 30 * 24 * 3600 * 1000);

  const [elRefs, setElRefs] = useState<ElRefsType>({});

  useEffect(() => {
    const obj = Object.fromEntries(
      events.map((ev) => [`event-${ev.timestamp}`, createRef()]),
    );
    // a bit of a hack... createRef() by definition is of type "unknown"
    setElRefs(obj as ElRefsType);
  }, [events]);

  const horizontalScrollRef = useRef<HTMLDivElement | null>(null);
  const { isSwiping } = useDragScroll({
    sliderRef: horizontalScrollRef,
  });

  const [distance, setDistance] = useState(0);

  const pageWidth = useWindowSize().width;
  useEffect(() => {
    if (pageWidth < 600) {
      setDistance(Math.abs((pageWidth / events.length) * 1.5));
    } else if (pageWidth < 1100) {
      setDistance(Math.abs((pageWidth / events.length) * 0.5));
    } else {
      setDistance(Math.abs((pageWidth / events.length) / 4));
    }
  }, [pageWidth]);
  const classes = useStyles({ isSwiping });
  return (
    <>
      <Typography variant="header">
        {t('markets.detailView.timeline.title')}
      </Typography>
      <Spacer spacing={2} />
      {/* horizontal timeline */}
      <div className={classes.horizontalTimelineRoot} ref={horizontalScrollRef}>
        {/* years */}
        {YEARS.filter(
          (year) => year > eventsReverse[0].timestamp && year < today,
        ).map((year, index) => (
          <Typography
            variant="secondary2bold"
            noWrap
            key={year}
            style={{
              fontWeight: 700,
              position: 'absolute',
              direction: 'ltr',
              bottom: 0,
              right: `${
                (Math.log(Math.abs(year - today)) - Math.log(Math.abs(events[0].timestamp - today))) * distance
              }%`,
            }}
          >
            {new Date(year).toLocaleDateString('en', { year: 'numeric' })}
            {' '}
            ❯
          </Typography>
        ))}
        {/* today */}
        <div
          style={{
            flex: '0 0 auto',
            direction: 'ltr',
            // width: `${distance}%`,
          }}
        >
          <div
            className={clsx(
              classes.horizontalTimelineBar,
              classes.horizontalTimelineBarCurrentRound,
              classes.horizontalTimelineBarLastItem,
            )}

          />
          <Typography variant="secondary2">
            {t('markets.detailView.timeline.now')}
          </Typography>
        </div>
        {/* other events */}
        {[...events].sort(
          (a, b) => b.timestamp - a.timestamp,
        ).map((event, index) => {
          // closest distance equals 3%
          const timespanDistance = Math.log(Math.abs(event.timestamp - today));

          const prevDistance = index === 0
            ? event.timestamp
            : Math.log(Math.abs(events[index - 1].timestamp - today));
          // const timespanDistance = Math.abs(event.timestamp - today);
          // : Math.abs(event.timestamp - events[index - 1].timestamp);
          const spacer = `${
            (timespanDistance - prevDistance) * distance
          }%`;
          return (
            <div
              key={event.timestamp}
              style={{
                // position: 'absolute',
                // top: 0,
                // bottom: 0,
                // right: spacer,
                flex: '0 0 auto',
                direction: 'ltr',
                minWidth: spacer,
              }}
            >
              {/* bubbles */}
              {event.items.map((item) => (
                <IconButton
                  key={item.id}
                  className={clsx(
                    classes.horizontalTimelineIcon,
                    event.isCurrentRound
                      ? classes.horizontalTimelineIconCurrentRound
                      : classes.horizontalTimelineIconLastRounds,
                  )}
                  onClick={() => {
                    const y = elRefs[`event-${event.timestamp}`]?.current?.getBoundingClientRect().top + window.pageYOffset - 65;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                    // elRefs[`event-${event.timestamp}`]?.current?.scrollIntoView(
                    //   {
                    //     behavior: 'smooth', block: 'nearest', inline: 'end',
                    //   }
                    // );
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SVGIcon eventType={item.eventType} />
                  </div>
                </IconButton>
              ))}
              {/* vertical line */}
              <div
                className={clsx(
                  classes.horizontalTimelineVerticalBar,
                  event.isCurrentRound
                    && classes.horizontalTimelineVerticalBarCurrentRound,
                )}
              />
              {/* horizontal bar */}
              <div
                className={clsx(
                  classes.horizontalTimelineBar,
                  event.isCurrentRound
                    && classes.horizontalTimelineBarCurrentRound,
                  index === events.length - 1
                    && classes.horizontalTimelineBarFirstItem,
                )}
              />
              {/* month */}
              <Typography variant="secondary2">
                {new Date(event.timestamp).toLocaleDateString('en', {
                  month: 'short',
                })}
              </Typography>
            </div>
          );
        })}
      </div>

      {/* vertical timeline 1: during current round */}
      <Spacer spacing={4} />
      <Typography
        variant={sm ? 'subheader2' : 'title2'}
        style={{ color: theme.palette.primary.dark }}
      >
        {t('markets.detailView.timeline.currentRound')}
      </Typography>
      <Spacer spacing={3} />
      <VerticalTimeline
        elRefs={elRefs}
        events={[...events].sort(
          (a, b) => b.timestamp - a.timestamp,
        ).filter((x) => x.isCurrentRound)}
      />

      {/* vertical timeline 2: before current round */}
      {events.filter((x) => !x.isCurrentRound).length > 0 && (
      <>
        <Spacer spacing={4} />
        <Typography
          variant={sm ? 'subheader2' : 'title2'}
          style={{ color: theme.palette.primary.dark }}
        >
          {t('markets.detailView.timeline.previousRound')}
        </Typography>
        <Spacer spacing={3} />
        <VerticalTimeline
          elRefs={elRefs}
          events={[...events].sort(
            (a, b) => b.timestamp - a.timestamp,
          ).filter((x) => !x.isCurrentRound)}
        />
      </>
      )}
    </>
  );
};

Timeline.defaultProps = (defaultProps as unknown) as Partial<Props>;

export default Timeline;
